import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { API_ROUTES } from '../consts/api-routes.const';
import { HttpService } from './http.service';

/** Address block of the confirm-appointment customerInfo (PlanIt contact data). */
export interface ConfirmAppointmentAddress {
  street?: string;
  houseNumber?: string;
  houseNumberSuffix?: string;
  zipCode?: string;
  city?: string;
}

/** Newsletter opt-ins; the backend forwards a positive brandNewsletter to the ImporterLead API. */
export interface ConfirmAppointmentNewsletters {
  brandNewsletter: boolean;
}

/** customerInfo block — maps the booking's appointment-details + session mileage. */
export interface ConfirmAppointmentCustomerInfo {
  firstName?: string;
  // PlanIt calls the surname "name".
  name?: string;
  email?: string;
  mileage?: string;
  phoneNumber?: string;
  licensePlate?: string;
  address?: ConfirmAppointmentAddress;
  newsletters?: ConfirmAppointmentNewsletters;
}

/** appointmentInfo block — the chosen date/time + optional replacement vehicle. */
export interface ConfirmAppointmentInfo {
  // YYYY-MM-DD; required by the backend (Assert\NotBlank + Assert\Date).
  date: string;
  time?: string;
  // Vrije opmerking van de klant voor de vestiging; gaat 1-op-1 door naar PlanIt.
  remarks?: string;
  replacementVehicle?: number;
  willBeWaiting: boolean;
}

/**
 * Body for POST /wp/planner/appointment (backend PostConfirmAppointmentPayload).
 * `jobs`/`extraInfo` are intentionally omitted: the selected work items already
 * live in the server-side form-state stored at step 1 (POST /wp/planner/jobs),
 * and the booking flow keeps no PlanIt job IDs to rebuild that array.
 */
export interface ConfirmAppointmentRequest {
  establishmentId: number;
  appointmentInfo?: ConfirmAppointmentInfo;
  customerInfo?: ConfirmAppointmentCustomerInfo;
  source?: string;
}

/** Response of POST /wp/planner/appointment (backend ConfirmAppointment). */
export interface ConfirmAppointmentResponse {
  appointmentId: number | null;
  additionalInformation: string | null;
}

@Injectable({ providedIn: 'root' })
export class AppointmentService {
  private readonly httpService: HttpService = inject(HttpService);

  /**
   * Confirms the workshop appointment. The active license plate is resolved
   * server-side from the session (established by the step-1 jobs submit) and the
   * `x-license-plate` header the interceptor adds, so it is not part of the body.
   */
  public confirm(payload: ConfirmAppointmentRequest): Observable<ConfirmAppointmentResponse> {
    return this.httpService.post<ConfirmAppointmentResponse>(
      API_ROUTES.PLANNER.appointment(),
      payload,
    );
  }
}
